import { useEffect, useMemo, useRef, useState } from 'react';
import { condividiReport } from '../lib/condivisione';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  fetchSavedSearches, createSavedSearch, deleteSavedSearch, updateSavedSearch, fetchRecentListings, fetchNearbyListings, fetchPlaceBoundary, markSearchSeen, togglePreferito, api,
} from '../lib/api';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import ProspectCard from '../components/ProspectCard';
import { openMapAroundMe } from '../lib/geo';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { TIPOLOGIA_CATEGORIES } from '../components/FilterPanel';
import ZonePickerMap from '../components/ZonePickerMap';
import AreaPreview from '../components/AreaPreview';

const FONT = 'Manrope, system-ui, sans-serif';

// Encoding compatto di un'area disegnata per l'URL: "lat,lng;lat,lng;…".
const encodeArea = (path) => (path || []).map((p) => `${(+p.lat).toFixed(5)},${(+p.lng).toFixed(5)}`).join(';');
const decodeArea = (str) => !str ? null : String(str).split(';')
  .map((pair) => { const [la, lo] = pair.split(',').map(Number); return { lat: la, lng: lo }; })
  .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
// Viewport "south,west,north,east" ⇄ {south,west,north,east}
const encodeVp = (vp) => vp ? `${vp.south},${vp.west},${vp.north},${vp.east}` : null;
const decodeVp = (str) => {
  if (!str) return null;
  const [s, w, n, e] = String(str).split(',').map(Number);
  return [s, w, n, e].every(Number.isFinite) ? { south: s, west: w, north: n, east: e } : null;
};

const VERDETTI = [
  { v: 'eccezionale', label: 'Eccezionale', color: '#16a34a' },
  { v: 'consigliata', label: 'Consigliata', color: '#65a30d' },
  { v: 'con_riserva', label: 'Con riserva', color: '#d97706' },
  { v: 'da_evitare',  label: 'Da evitare',  color: '#dc2626' },
];
const PREZZI = [50000, 100000, 150000, 200000, 300000, 500000];
const MQ = [40, 60, 80, 120];
const euro = (v) => { try { return '€ ' + new Intl.NumberFormat('it-IT').format(v); } catch { return '€ ' + v; } };

// Config dei filtri del costruttore (collassati → sheet, come sulla mappa).
const BUILDER_FILTERS = {
  verdetto:   { title: 'Verdetto Rivelasta', multi: true,  options: VERDETTI.map((x) => ({ v: x.v, label: x.label, color: x.color })) },
  tipologia:  { title: 'Tipologia',          multi: true,  options: TIPOLOGIA_CATEGORIES.map((t) => ({ v: t.id, label: t.label })) },
  prezzo:     { title: 'Prezzo massimo',     multi: false, options: PREZZI.map((p) => ({ v: String(p), label: euro(p) })) },
  superficie: { title: 'Superficie minima',  multi: false, options: MQ.map((m) => ({ v: String(m), label: 'da ' + m + ' m²' })) },
};

// Riga filtro collassata: mostra solo il nome + il valore scelto; tap → apre lo sheet.
function FilterRow({ label, value, placeholder = 'Tutti', onClick, last }) {
  const has = !!(value && value.length);
  return (
    <button onClick={onClick} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '15px 14px',
      border: 0, borderBottom: last ? 0 : '1px solid #EEF0F2', background: 'transparent',
      cursor: 'pointer', textAlign: 'left', fontFamily: FONT,
    }}>
      <span style={{ flex: '0 0 auto', fontSize: 15, fontWeight: 800, color: '#1F2937' }}>{label}</span>
      <span style={{ flex: 1, textAlign: 'right', fontSize: 14, fontWeight: 600, color: has ? '#1F6FEB' : '#9AA0A6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {has ? value : placeholder}
      </span>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B9BEC5" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 auto' }}><path d="M9 6l6 6-6 6" /></svg>
    </button>
  );
}

// Bottom sheet per scegliere il valore di un filtro (multi chip o single lista).
function BuilderSheet({ filter, multiSelected, singleValue, onToggleMulti, onSelectSingle, onClear, onClose }) {
  const isMulti = filter.multi;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 5300 }} />
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 5301, background: '#fff',
        borderTopLeftRadius: 20, borderTopRightRadius: 20, boxShadow: '0 -8px 40px rgba(0,0,0,0.22)',
        maxHeight: '72vh', display: 'flex', flexDirection: 'column', fontFamily: FONT,
        animation: 'bsUp .22s cubic-bezier(.22,.61,.36,1)',
      }}>
        <style>{'@keyframes bsUp{from{transform:translateY(100%)}to{transform:translateY(0)}}'}</style>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
          <span style={{ width: 40, height: 5, borderRadius: 3, background: '#DDE1E6' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 18px 10px' }}>
          <span style={{ fontSize: 17, fontWeight: 800, color: '#1F2937' }}>{filter.title}</span>
          <button onClick={onClose} aria-label="Chiudi" style={{ border: 0, background: '#F1F3F5', width: 30, height: 30, borderRadius: '50%', cursor: 'pointer', fontSize: 16, color: '#6B7280' }}>{'✕'}</button>
        </div>
        <div style={{ overflowY: 'auto', padding: '4px 12px', paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}>
          {isMulti ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '4px 6px 8px' }}>
              {filter.options.map((o) => {
                const active = (multiSelected || []).includes(o.v);
                return (
                  <button key={o.v} onClick={() => onToggleMulti(o.v)} style={{
                    padding: '9px 14px', borderRadius: 999, cursor: 'pointer', fontSize: 13.5, fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    border: `1.5px solid ${active ? (o.color || '#1F2937') : '#E4E6EA'}`,
                    background: active ? (o.color || '#1F2937') : '#fff', color: active ? '#fff' : '#3A3F45',
                  }}>
                    {o.color && <span style={{ width: 9, height: 9, borderRadius: '50%', background: active ? '#fff' : o.color }} />}
                    {o.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <>
              <button onClick={() => onSelectSingle('')} style={sheetRow(!singleValue)}>
                <span>Qualsiasi</span>{!singleValue && <Chk />}
              </button>
              {filter.options.map((o) => {
                const active = singleValue === o.v;
                return (
                  <button key={o.v} onClick={() => onSelectSingle(o.v)} style={sheetRow(active)}>
                    <span>{o.label}</span>{active && <Chk />}
                  </button>
                );
              })}
            </>
          )}
        </div>
        {isMulti && (
          <div style={{ display: 'flex', gap: 10, padding: '10px 16px', paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid #EEF0F2' }}>
            <button onClick={onClear} disabled={!(multiSelected || []).length} style={{
              flex: '0 0 auto', padding: '11px 16px', borderRadius: 12, border: '1.5px solid #E4E6EA', background: '#fff',
              color: (multiSelected || []).length ? '#3A3F45' : '#B9BEC5', fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}>Cancella</button>
            <button onClick={onClose} style={{ flex: 1, padding: '11px 16px', borderRadius: 12, border: 0, background: '#1F6FEB', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>Fatto</button>
          </div>
        )}
      </div>
    </>
  );
}
const sheetRow = (active) => ({
  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '13px 14px', margin: '2px 0', borderRadius: 12, cursor: 'pointer',
  border: `1.5px solid ${active ? '#1F6FEB' : 'transparent'}`, background: active ? '#EDF3FE' : '#fff',
  fontSize: 15, fontWeight: 700, color: '#22272E', textAlign: 'left', fontFamily: FONT,
});
function Chk() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1F6FEB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>);
}

// Costruisce una query-string dai params salvati.
function paramsToQuery(params) {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, val]) => {
    if (val == null || val === '') return;
    q.set(k, Array.isArray(val) ? val.join(',') : String(val));
  });
  return q.toString();
}

// Etichetta riassuntiva dei filtri per la card.
function summarize(params) {
  const bits = [];
  if (params.verdetto) {
    const keys = String(params.verdetto).split(',');
    const labels = keys.map((k) => VERDETTI.find((x) => x.v === k)?.label || k);
    bits.push(labels.join(', '));
  }
  if (params.tipologia_categoria) {
    const keys = String(params.tipologia_categoria).split(',');
    const labels = keys.map((k) => TIPOLOGIA_CATEGORIES.find((t) => t.id === k)?.label || k);
    bits.push(labels.join(', '));
  }
  if (params.prezzo_max) bits.push('fino a ' + euro(params.prezzo_max));
  if (params.mq_min) bits.push('da ' + params.mq_min + ' m²');
  return bits.length ? bits.join(' · ') : 'Tutti gli immobili';
}

// Filtri come chip (per la card): {label, color?}. Ordine: verdetto, tipologia, prezzo, mq.
function filterChips(params) {
  const chips = [];
  if (params.verdetto) {
    String(params.verdetto).split(',').forEach((k) => {
      const v = VERDETTI.find((x) => x.v === k);
      if (v) chips.push({ label: v.label, color: v.color });
    });
  }
  if (params.tipologia_categoria) {
    String(params.tipologia_categoria).split(',').forEach((k) => {
      const t = TIPOLOGIA_CATEGORIES.find((x) => x.id === k);
      if (t) chips.push({ label: t.label });
    });
  }
  if (params.prezzo_max) chips.push({ label: 'fino a ' + euro(params.prezzo_max) });
  if (params.mq_min) chips.push({ label: 'da ' + params.mq_min + ' m²' });
  if (params.mq_max) chips.push({ label: 'fino a ' + params.mq_max + ' m²' });
  return chips;
}

export default function ProspectHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const ctx = useOutletContext?.() || {};
  const toggleSidebar = ctx.toggleSidebar;

  const [searches, setSearches] = useState(null); // null = loading
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderMode, setBuilderMode] = useState('zone'); // 'zone' | 'draw'
  const [builderSimple, setBuilderSimple] = useState(false); // true = solo zona, niente filtri
  const openBuilder = (mode, simple = false) => { setBuilderMode(mode || 'zone'); setBuilderSimple(simple); setBuilderOpen(true); };
  const searchAroundMe = () => {
    if (!('geolocation' in navigator)) return openBuilder('zone');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        const d = 0.05; // ~5-6 km attorno all'utente
        const vp = { south: lat - d, west: lng - d * 1.4, north: lat + d, east: lng + d * 1.4 };
        openSearch({ center_lat: lat, center_lng: lng, center_label: 'Intorno a te', vp: encodeVp(vp) });
      },
      () => openBuilder('zone'),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  };
  const [pendingDelete, setPendingDelete] = useState(null); // ricerca in attesa di conferma eliminazione
  const [alertsPrompt, setAlertsPrompt] = useState(null);   // { search, params } → modale avvisi post-salvataggio

  const [recent, setRecent] = useState([]);
  const [nearby, setNearby] = useState([]);       // annunci vicino a te (geoloc, di default)
  const [recentReportUrl, setRecentReportUrl] = useState(null);
  const toggleRecentFav = (row) => {
    const flip = (l) => l.map((x) => x.id === row.id ? { ...x, preferito: !x.preferito } : x);
    setRecent(flip);
    togglePreferito(row.id).catch(() => setRecent(flip));
  };
  const toggleNearbyFav = (row) => {
    const flip = (l) => l.map((x) => x.id === row.id ? { ...x, preferito: !x.preferito } : x);
    setNearby(flip);
    togglePreferito(row.id).catch(() => setNearby(flip));
  };
  const [nearbyLoading, setNearbyLoading] = useState(true);
  // Annunci vicino a te. PERF (2026-07-14): non blocchiamo sulla geolocalizzazione —
  // se abbiamo l'ULTIMA posizione nota carichiamo subito (istantaneo), poi aggiorniamo
  // con la posizione reale in background.
  useEffect(() => {
    let cached = null;
    try { cached = JSON.parse(localStorage.getItem('rvl_lastgeo') || 'null'); } catch { /* noop */ }
    if (cached && cached.lat) {
      fetchNearbyListings(cached.lat, cached.lng).then((l) => { setNearby(l); setNearbyLoading(false); }).catch(() => {});
    }
    if (!('geolocation' in navigator)) { setNearbyLoading(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        try { localStorage.setItem('rvl_lastgeo', JSON.stringify({ lat, lng })); } catch { /* noop */ }
        fetchNearbyListings(lat, lng).then((l) => { setNearby(l); setNearbyLoading(false); }).catch(() => setNearbyLoading(false));
      },
      () => setNearbyLoading(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  }, []);

  // Immobili già "sbloccati DA ME" (prospect_views): solo per questi la card mostra
  // subito il verdetto; gli altri restano da analizzare (reveal al click). 2026-07-14.
  const [analyzedIds, setAnalyzedIds] = useState(() => new Set());
  const [capModalOpen, setCapModalOpen] = useState(false);
  useEffect(() => {
    api.get('/api/prospect/analyzed').then(({ data }) => setAnalyzedIds(new Set(data?.ids || []))).catch(() => {});
  }, []);
  // Cap-check + registrazione dell'analisi (come sulla mappa). Ritorna la modalità
  // che la card usa: 'reveal' (verdetto noto → animazione) | 'requested' | false.
  const homeAnalyze = async (row) => {
    // Immobile GIÀ analizzato: il reveal parte ISTANTANEAMENTE al tap; la POST di
    // registrazione vista è best-effort in background (niente delay). Il cap vale
    // solo sull'AVVIO di una nuova analisi (ramo sotto). Marina 2026-07-14.
    if (row.rivelasta_verdetto) {
      setAnalyzedIds((s) => new Set([...s, row.id]));
      api.post(`/api/immobili/${row.id}/prospect-analyze`).catch(() => {});
      return 'reveal';
    }
    try {
      await api.post(`/api/immobili/${row.id}/prospect-request-analysis`);
      return 'requested';
    } catch (e) {
      if (e?.response?.status === 403) setCapModalOpen(true);
      return false;
    }
  };
  const reload = () => fetchSavedSearches().then(setSearches).catch(() => setSearches([]));
  useEffect(() => {
    reload();
    fetchRecentListings().then(setRecent).catch(() => setRecent([]));
    // Preload dell'API Google Maps mentre l'utente è in Home → aprendo la mappa
    // dalla tab bar è (quasi) istantanea. Marina 2026-07-14.
    const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (key && !(window.google && window.google.maps)) {
      try { setOptions({ key, v: 'weekly' }); } catch { /* già configurato */ }
      importLibrary('maps').catch(() => {});
      importLibrary('marker').catch(() => {});
    }
  }, []);

  const openSearch = (params, scanning, sid, since) => {
    const extra = {};
    if (scanning) extra.scanning = 1;
    if (sid) extra.sid = sid;
    if (since) extra.since = since; // soglia per evidenziare i nuovi (azzurro) sulla mappa
    const qs = paramsToQuery({ ...params, ...extra });
    navigate(`/app/mappa${qs ? '?' + qs : ''}`);
  };
  // Apre una ricerca e la segna come "vista" (azzera i nuovi annunci).
  const openAndSeen = (s) => {
    const since = s.seen_at; // soglia "nuovi" PRIMA di marcare come visto
    if (s.new_count > 0) {
      markSearchSeen(s.id).catch(() => {});
      setSearches((list) => list.map((x) => (x.id === s.id ? { ...x, new_count: 0 } : x)));
    }
    openSearch(s.params, s.scan_pending, s.id, since);
  };

  const onSaved = async (params, label) => {
    let search = null;
    // Avvisi WhatsApp ATTIVI di default su ogni nuova ricerca: niente più modale che
    // chiede se attivarli. L'utente può disattivarli dalla card (icona WhatsApp) o
    // seguendo le istruzioni in ogni messaggio. Marina/Michael 2026-07-14.
    try { search = await createSavedSearch({ label, params, alerts_enabled: true }); } catch { /* best effort */ }
    setBuilderOpen(false);
    openSearch(params, search?.scan_pending, search?.id);
  };

  // Decisione dal modale avvisi post-salvataggio → vai SUBITO sulla mappa
  // (l'attivazione avvisi va in background, così non resti fermo sulla Home).
  const resolveAlertsPrompt = (enable) => {
    const ap = alertsPrompt;
    setAlertsPrompt(null);
    if (!ap) return;
    if (enable) updateSavedSearch(ap.search.id, { alerts_enabled: true }).catch(() => {});
    openSearch(ap.params, ap.search?.scan_pending, ap.search?.id);
  };

  const toggleAlert = async (s) => {
    const next = !s.alerts_enabled;
    setSearches((list) => list.map((x) => (x.id === s.id ? { ...x, alerts_enabled: next } : x)));
    try { await updateSavedSearch(s.id, { alerts_enabled: next }); } catch { reload(); }
  };
  const remove = async (s) => {
    setPendingDelete(null);
    setSearches((list) => list.filter((x) => x.id !== s.id));
    try { await deleteSavedSearch(s.id); } catch { reload(); }
  };

  const firstName = (user?.name || '').split(' ')[0];

  // Effetto scroll stile immobiliare: la parte alta dell'hero (titolo + CTA)
  // sbiadisce e collassa; la barra "Ricerca avanzata" resta ancorata in cima e
  // si allarga leggermente. heroP: 0 (in cima) → 1 (scrollato). Marina 2026-07-14.
  const scrollRef = useRef(null);
  const tickingRef = useRef(false);
  const [heroP, setHeroP] = useState(0);
  const onHeroScroll = (e) => {
    const st = e.currentTarget.scrollTop;
    if (tickingRef.current) return;
    tickingRef.current = true;
    requestAnimationFrame(() => {
      setHeroP(Math.max(0, Math.min(1, st / 150)));
      tickingRef.current = false;
    });
  };

  return (
    <div ref={scrollRef} onScroll={onHeroScroll} style={{ flex: 1, minHeight: 0, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#F6F7F9', fontFamily: FONT }}>
      {/* Hero blu: scorre normalmente e SBIADISCE (solo opacity, niente resize →
          nessun shift/glitch). */}
      <div style={{
        background: 'linear-gradient(160deg, #1553C7 0%, #1F6FEB 55%, #1657CC 100%)',
        color: '#fff', padding: '18px 20px 46px',
        paddingTop: 'calc(18px + env(safe-area-inset-top, 0px))',
        borderBottomLeftRadius: 26, borderBottomRightRadius: 26,
        opacity: Math.max(0, 1 - heroP * 1.1),
        pointerEvents: heroP > 0.7 ? 'none' : 'auto',
      }}>
        <div style={{ marginTop: 22, textAlign: 'center' }}>
          {/* Logo orizzontale (casetta + scritta), versione bianca per l'hero blu.
              display:block + margin auto → nessuno spazio residuo sotto l'immagine. */}
          <img src="/rivelasta-logo-h-white.png" alt="Rivelasta"
               style={{ width: 'min(72vw, 270px)', height: 'auto', display: 'block', margin: '0 auto' }} />
          <div style={{ marginTop: 12, fontSize: 16, fontWeight: 700, opacity: 0.95, lineHeight: 1.3 }}>
            Il n°1 per trovare gli affari all'asta.
          </div>
        </div>

        {/* CTA principale: mini-tutorial guidato — stesso tasto nero glowing dell'analisi AI */}
        <button className="pr-cardbtn pr-cardbtn--analyze" onClick={() => navigate('/app/mappa?tutorial=1')} style={{ marginTop: 22, marginLeft: 10, marginRight: 10, width: 'calc(100% - 20px)' }}>
          <span className="pr-wand">
            <svg className="pr-wand__main" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3.5 20.5L13 11" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><path d="M16 3.8L17.4 6.6L20.2 8L17.4 9.4L16 12.2L14.6 9.4L11.8 8L14.6 6.6Z" fill="#fff"/></svg>
            <svg className="pr-wand__twinkle" width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M12 3L13.4 9.6L20 11L13.4 12.4L12 19L10.6 12.4L4 11L10.6 9.6Z" fill="#4FD89E"/></svg>
          </span>
          <span>Scopri come funziona</span>
        </button>
      </div>

      {/* Campo "Ricerca avanzata" — sticky: si sovrappone all'hero e resta ANCORATO
          in cima quando il resto sbiadisce/scorre via. Nessun resize → nessun shift. */}
      <div style={{
        position: 'sticky', top: 'calc(8px + env(safe-area-inset-top, 0px))', zIndex: 21,
        // parte un po' più stretto (padding 30) e si allarga fino alla larghezza
        // piena (padding 20) man mano che scrolli e diventa sticky. Solo orizzontale
        // → nessun reflow verticale, nessun glitch.
        padding: `0 ${30 - 10 * heroP}px`, marginTop: -32,
      }}>
        <button onClick={() => openBuilder('zone', false)} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          background: '#fff', border: 0, borderRadius: 16, padding: '15px 16px',
          cursor: 'pointer', boxShadow: '0 10px 30px rgba(0,0,0,0.18)', textAlign: 'left',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1F6FEB" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
          <span style={{ flex: 1, color: '#1F2937', fontWeight: 700, fontSize: 15.5 }}>Ricerca avanzata</span>
          <span style={{ color: '#1F6FEB', fontWeight: 800, fontSize: 13 }}>Filtra ›</span>
        </button>
      </div>

      {/* Le tue ricerche */}
      <div style={{ padding: '22px 18px calc(96px + env(safe-area-inset-bottom, 0px))' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: '#1F2937' }}>Le tue ricerche</div>
          {Array.isArray(searches) && searches.length > 0 && (
            <button onClick={() => openBuilder('zone', false)} style={{ border: 0, background: 'transparent', color: '#1F6FEB', fontWeight: 800, fontSize: 13.5, cursor: 'pointer' }}>+ Nuova</button>
          )}
        </div>

        {/* Le 3 card fisse (disegna / zona / intorno a te) compaiono SUBITO, anche
            mentre `searches` è in caricamento — sono statiche. Le card delle ricerche
            salvate le rimpiazzano solo se l'utente ne ha. 2026-07-14. */}
        {(searches === null || (Array.isArray(searches) && searches.length === 0)) && (
          <div className="pr-hscroll" style={{
            display: 'flex', gap: 12, overflowX: 'auto', scrollSnapType: 'x mandatory',
            margin: '0 -18px', padding: '2px 18px 6px', scrollPaddingLeft: 18,
            scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
          }}>
            <style>{'.pr-hscroll::-webkit-scrollbar{display:none}'}</style>
            {[
              { key: 'zone', title: 'Ricerca per zona o città', desc: 'Cerca un comune, un quartiere o un indirizzo.', bg: '#E9F7EF', fg: '#16915E',
                icon: (c) => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /><path d="M9 9h.01M9 12h.01M9 15h.01M9 18h.01" /></svg>),
                onClick: () => openBuilder('zone', true) },
              { key: 'draw', title: "Disegna un'area sulla mappa", desc: 'Traccia tu i confini della zona che ti interessa.', bg: '#EAF2FF', fg: '#1F6FEB',
                icon: (c) => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><circle cx="11" cy="11" r="2" /></svg>),
                onClick: () => openBuilder('draw', true) },
              { key: 'around', title: 'Ricerca intorno a te', desc: 'Usa la tua posizione per vedere le aste vicine.', bg: '#FDEEE9', fg: '#C2453B',
                icon: (c) => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2.6" fill={c} stroke="none" /><path d="M12 1.5v3M12 19.5v3M1.5 12h3M19.5 12h3" /></svg>),
                onClick: searchAroundMe },
            ].map((c) => (
              <button key={c.key} onClick={c.onClick} style={{
                flex: '0 0 auto', width: 'min(72vw, 228px)', scrollSnapAlign: 'start', textAlign: 'left',
                background: '#fff', border: '1px solid #EEF0F2', borderRadius: 18, padding: 18, cursor: 'pointer',
                boxShadow: '0 2px 12px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: 9, minHeight: 152,
              }}>
                <div style={{ width: 46, height: 46, borderRadius: 13, background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 2 }}>
                  {c.icon(c.fg)}
                </div>
                <div style={{ fontSize: 15.5, fontWeight: 800, color: '#1F2937', lineHeight: 1.25, letterSpacing: '-0.01em' }}>{c.title}</div>
                <div style={{ fontSize: 12.5, color: '#6B7280', lineHeight: 1.35 }}>{c.desc}</div>
              </button>
            ))}
          </div>
        )}

        {Array.isArray(searches) && searches.length > 0 && (
        <div className="pr-hscroll" style={{
          display: 'flex', gap: 12, overflowX: 'auto', scrollSnapType: 'x mandatory',
          margin: '0 -18px', padding: '2px 18px 6px', scrollPaddingLeft: 18,
          scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
        }}>
          <style>{'.pr-hscroll::-webkit-scrollbar{display:none}'}</style>
        {searches.map((s) => {
          const sArea = decodeArea(s.params.area);
          const sVp = !sArea ? decodeVp(s.params.vp) : null;
          const sCenter = (!sArea && !sVp && s.params.center_lat)
            ? { lat: +s.params.center_lat, lng: +s.params.center_lng } : null;
          const sItaly = !sArea && !sVp && !sCenter; // ricerca senza zona = tutta Italia
          return (
          <div key={s.id} style={{
            background: '#fff', borderRadius: 16, padding: 16, overflow: 'hidden',
            flex: '0 0 auto', width: 'min(80vw, 310px)', scrollSnapAlign: 'start',
            boxShadow: '0 2px 14px rgba(0,0,0,0.08)',
          }}>
            <div style={{ margin: '-16px -16px 12px', position: 'relative' }}>
              <AreaPreview area={sArea} bounds={sVp} center={sCenter} italy={sItaly} height={130}
                radius="16px 16px 0 0" bordered={false}
                onClick={() => openAndSeen(s)} badge={null} />
              {/* WhatsApp avvisi — in alto a sinistra */}
              <button onClick={(e) => { e.stopPropagation(); toggleAlert(s); }} aria-pressed={s.alerts_enabled} aria-label="Avvisi WhatsApp" style={{
                position: 'absolute', left: 10, top: 10, zIndex: 2, width: 34, height: 34, borderRadius: '50%',
                border: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: s.alerts_enabled ? '#25D366' : 'rgba(255,255,255,0.95)', boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill={s.alerts_enabled ? '#fff' : '#8A9099'}><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.8 14.06c-.25.7-1.44 1.33-1.99 1.41-.51.08-1.16.11-1.87-.12-.43-.14-.98-.32-1.69-.62-2.97-1.28-4.91-4.27-5.06-4.47-.15-.2-1.21-1.61-1.21-3.07s.77-2.18 1.04-2.48c.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.6.84 2.06.91 2.21.07.15.12.32.02.52-.1.2-.15.32-.3.49-.15.17-.31.39-.45.52-.15.15-.3.31-.13.61.17.3.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.65-.07.18-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.27.1 1.71.81 2.01.96.3.15.5.22.57.35.07.12.07.72-.18 1.42z"/></svg>
              </button>
              {/* Cestino — in alto a destra */}
              <button onClick={(e) => { e.stopPropagation(); setPendingDelete(s); }} aria-label="Elimina" style={{
                position: 'absolute', right: 10, top: 10, zIndex: 2, width: 34, height: 34, borderRadius: '50%',
                border: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(255,255,255,0.95)', boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C2453B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
              </button>
              {s.scan_pending && (
                <div style={{
                  position: 'absolute', left: 10, bottom: 10, zIndex: 2, display: 'inline-flex', alignItems: 'center', gap: 7,
                  background: 'rgba(31,111,235,0.95)', color: '#fff', borderRadius: 999, padding: '5px 10px',
                  fontSize: 11.5, fontWeight: 800, boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                }}>
                  <span style={{
                    width: 11, height: 11, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.45)',
                    borderTopColor: '#fff', display: 'inline-block', animation: 'ssSpin .8s linear infinite',
                  }} />
                  <style>{'@keyframes ssSpin{to{transform:rotate(360deg)}}'}</style>
                  Scansione in corso…
                </div>
              )}
            </div>

            <div onClick={() => openAndSeen(s)} style={{ cursor: 'pointer' }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{String(s.label || '').split(' · ')[0]}</div>
              {(() => {
                const chips = filterChips(s.params);
                const shown = chips.slice(0, 4);
                const more = chips.length - shown.length;
                if (chips.length === 0) {
                  return <div style={{ color: '#9AA0A6', fontSize: 12.5, marginTop: 8, fontWeight: 600 }}>Tutti gli immobili</div>;
                }
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                    {shown.map((c, i) => (
                      <span key={i} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700,
                        color: '#3A3F45', background: '#F1F3F5', borderRadius: 999, padding: '5px 10px',
                      }}>
                        {c.color && <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.color }} />}
                        {c.label}
                      </span>
                    ))}
                    {more > 0 && (
                      <span style={{ fontSize: 12, fontWeight: 800, color: '#1F6FEB', background: '#EAF2FF', borderRadius: 999, padding: '5px 10px' }}>+{more} altri</span>
                    )}
                  </div>
                );
              })()}
            </div>

            {s.new_count > 0 && (
              <button onClick={(e) => { e.stopPropagation(); openAndSeen(s); }} style={{
                marginTop: 12, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: '#EAF2FF', color: '#1F6FEB', border: 0, borderRadius: 12, padding: '11px 14px',
                fontWeight: 800, fontSize: 14, cursor: 'pointer', fontFamily: FONT,
              }}>
                Nuovi annunci
                <span style={{
                  background: '#DC2626', color: '#fff', borderRadius: 999, minWidth: 22, height: 20,
                  padding: '0 6px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 900,
                }}>{s.new_count > 99 ? '99+' : s.new_count}</span>
              </button>
            )}
          </div>
          );
        })}
        </div>
        )}

        {/* Annunci recenti (ultimi 10 gg) che rientrano nelle tue ricerche */}
        {recent.length > 0 && (
          <div style={{ marginTop: 28 }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#1F2937', marginBottom: 4 }}>Annunci recenti</div>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 14 }}>Nuovi immobili usciti nelle tue zone negli ultimi 10 giorni</div>
            {recent.map((r) => (
              <div key={r.id} style={{ marginBottom: 12 }}>
                <ProspectCard
                  row={r}
                  selfReveal
                  analyzed={analyzedIds.has(r.id)}
                  onOpen={() => navigate(`/app/mappa?focus=${r.id}`)}
                  onAnalyze={homeAnalyze}
                  onReport={(url) => setRecentReportUrl(url)}
                  onShare={(row, btn) => condividiReport(row, btn)}
                  onToggleFav={toggleRecentFav}
                />
              </div>
            ))}
          </div>
        )}

        {/* Annunci vicino a te (geolocalizzati) — caricati di default */}
        {nearby.length > 0 && (
          <div style={{ marginTop: 28 }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#1F2937', marginBottom: 4 }}>Annunci vicino a te</div>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 14 }}>Le aste più vicine alla tua posizione</div>
            {nearby.map((r) => (
              <div key={r.id} style={{ marginBottom: 12 }}>
                <ProspectCard
                  row={r}
                  selfReveal
                  analyzed={analyzedIds.has(r.id)}
                  onOpen={() => navigate(`/app/mappa?focus=${r.id}`)}
                  onAnalyze={homeAnalyze}
                  onReport={(url) => setRecentReportUrl(url)}
                  onShare={(row, btn) => condividiReport(row, btn)}
                  onToggleFav={toggleNearbyFav}
                />
              </div>
            ))}
          </div>
        )}

        {/* Skeleton "vicino a te" durante il primo caricamento (invece del vuoto) */}
        {nearbyLoading && nearby.length === 0 && (
          <div style={{ marginTop: 28 }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#1F2937', marginBottom: 4 }}>Annunci vicino a te</div>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 14 }}>Le aste più vicine alla tua posizione</div>
            <style>{'@keyframes rvlShimmer{0%{opacity:.55}50%{opacity:1}100%{opacity:.55}}'}</style>
            {[0, 1].map((i) => (
              <div key={i} style={{ marginBottom: 12, background: '#fff', border: '1px solid #EEF0F2', borderRadius: 24, overflow: 'hidden', animation: 'rvlShimmer 1.2s ease-in-out infinite' }}>
                <div style={{ height: 168, background: '#EEF0F2' }} />
                <div style={{ padding: 16 }}>
                  <div style={{ height: 22, width: '45%', background: '#EEF0F2', borderRadius: 6, marginBottom: 10 }} />
                  <div style={{ height: 13, width: '75%', background: '#F1F3F5', borderRadius: 6, marginBottom: 14 }} />
                  <div style={{ height: 74, background: '#F5F6F8', borderRadius: 16 }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom tab bar persistente (stile immobiliare.it) — coerente con la mappa */}
      <nav style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 60,
        display: 'flex', background: '#fff', borderTop: '1px solid #ECEDEF',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)', fontFamily: FONT,
      }}>
        {[
          { id: 'cerca', label: 'Cerca', active: true, onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
            icon: (c) => (<svg width="23" height="23" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke={c} strokeWidth="1.9"/><path d="M21 21l-4.3-4.3" stroke={c} strokeWidth="1.9" strokeLinecap="round"/></svg>) },
          { id: 'mappa', label: 'Mappa', onClick: () => openMapAroundMe(navigate),
            icon: (c) => (<svg width="23" height="23" viewBox="0 0 24 24" fill="none"><path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" stroke={c} strokeWidth="1.8" strokeLinejoin="round"/><circle cx="12" cy="10" r="2.4" stroke={c} strokeWidth="1.8"/></svg>) },
          { id: 'preferiti', label: 'Preferiti', onClick: () => navigate('/app/mappa?view=preferiti'),
            icon: (c) => (<svg width="23" height="23" viewBox="0 0 24 24" fill="none"><path d="M12 21s-7-4.35-9.5-8.5C1 9.5 2.5 5.5 6.5 5.5c2 0 3.5 1.5 5.5 3.5 2-2 3.5-3.5 5.5-3.5 4 0 5.5 4 4 7C19 16.65 12 21 12 21z" stroke={c} strokeWidth="1.8" strokeLinejoin="round"/></svg>) },
          { id: 'analizzati', label: 'Analizzati', onClick: () => navigate('/app/analizzati'),
            icon: (c) => (<svg width="23" height="23" viewBox="0 0 24 24" fill="none"><path d="M4 5h16M4 12h16M4 19h10" stroke={c} strokeWidth="1.8" strokeLinecap="round"/><circle cx="19.5" cy="18.5" r="3" stroke={c} strokeWidth="1.8"/><path d="M21.7 20.7L23 22" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>) },
          { id: 'profilo', label: 'Profilo', onClick: () => navigate('/app/profilo'),
            icon: (c) => (<svg width="23" height="23" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke={c} strokeWidth="1.8"/><path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>) },
        ].map((t) => {
          const col = t.active ? '#1F6FEB' : '#9AA0A6';
          return (
            <button key={t.id} onClick={t.onClick} style={{
              flex: 1, border: 0, background: 'transparent', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '9px 0 8px', color: col,
            }}>
              {t.icon(col)}
              <span style={{ fontSize: 11, fontWeight: t.active ? 700 : 600, letterSpacing: '-0.01em' }}>{t.label}</span>
            </button>
          );
        })}
      </nav>

      {recentReportUrl && (
        <div className="pr-report-modal" role="dialog" aria-label="Report analisi AI">
          <div className="pr-report-modal__backdrop" onClick={() => setRecentReportUrl(null)} />
          {/* ✕ figlia del contenitore (fixed), NON dello sheet: sta nella fascia
              sopra il modale e non si sovrappone al verdetto. */}
          <button type="button" className="pr-report-modal__close" onClick={() => setRecentReportUrl(null)} aria-label="Chiudi">×</button>
          <div className="pr-report-modal__sheet">
            <iframe className="pr-report-modal__iframe" src={recentReportUrl} title="Analisi immobile" />
          </div>
        </div>
      )}

      {capModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 5200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }} onClick={() => setCapModalOpen(false)} />
          <div style={{ position: 'relative', background: '#fff', borderRadius: 20, padding: '28px 22px', maxWidth: 340, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 34, marginBottom: 8 }}>🔒</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#1F2937', marginBottom: 6 }}>Analisi esaurite</div>
            <div style={{ fontSize: 13.5, color: '#6B7280', lineHeight: 1.45, marginBottom: 18 }}>
              Hai usato tutte le analisi disponibili. Contattaci per sbloccarne altre.
            </div>
            <button onClick={() => setCapModalOpen(false)} style={{ border: 0, background: '#1F6FEB', color: '#fff', borderRadius: 12, padding: '11px 22px', fontWeight: 800, fontSize: 14.5, cursor: 'pointer' }}>Ho capito</button>
          </div>
        </div>
      )}

      {builderOpen && (
        <SearchBuilder initialMode={builderMode} simple={builderSimple} onClose={() => setBuilderOpen(false)} onRun={onSaved} />
      )}

      {/* Modale avvisi WhatsApp subito dopo il salvataggio della ricerca */}
      {alertsPrompt && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 5400, background: 'rgba(15,23,42,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: FONT,
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: '24px 22px', width: '100%', maxWidth: 360,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: '#EAFBF0', margin: '0 auto 14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="#25D366"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.8 14.06c-.25.7-1.44 1.33-1.99 1.41-.51.08-1.16.11-1.87-.12-.43-.14-.98-.32-1.69-.62-2.97-1.28-4.91-4.27-5.06-4.47-.15-.2-1.21-1.61-1.21-3.07s.77-2.18 1.04-2.48c.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.6.84 2.06.91 2.21.07.15.12.32.02.52-.1.2-.15.32-.3.49-.15.17-.31.39-.45.52-.15.15-.3.31-.13.61.17.3.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.65-.07.18-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.27.1 1.71.81 2.01.96.3.15.5.22.57.35.07.12.07.72-.18 1.42z"/></svg>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1F2937', marginBottom: 6 }}>Vuoi gli avvisi su WhatsApp?</div>
            <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.45, marginBottom: 20 }}>
              Ti scrivo su WhatsApp quando escono nuovi immobili per «{alertsPrompt.search?.label}». Puoi disattivarli quando vuoi dalla card.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => resolveAlertsPrompt(false)} style={{
                flex: 1, border: '1.5px solid #E4E6EA', background: '#fff', color: '#3A3F45',
                borderRadius: 12, padding: '13px', fontWeight: 800, fontSize: 14.5, cursor: 'pointer',
              }}>No, grazie</button>
              <button onClick={() => resolveAlertsPrompt(true)} style={{
                flex: 1, border: 0, background: '#25D366', color: '#fff',
                borderRadius: 12, padding: '13px', fontWeight: 800, fontSize: 14.5, cursor: 'pointer',
              }}>Sì, avvisami</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale conferma eliminazione ricerca */}
      {pendingDelete && (
        <div onClick={() => setPendingDelete(null)} style={{
          position: 'fixed', inset: 0, zIndex: 5400, background: 'rgba(15,23,42,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: FONT,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 20, padding: '24px 22px', width: '100%', maxWidth: 360,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center',
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: '50%', background: '#FDECEA', margin: '0 auto 14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#C2453B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1F2937', marginBottom: 6 }}>Eliminare la ricerca?</div>
            <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.4, marginBottom: 20 }}>
              «{pendingDelete.label}» verrà rimossa dalle tue ricerche salvate.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setPendingDelete(null)} style={{
                flex: 1, border: '1.5px solid #E4E6EA', background: '#fff', color: '#3A3F45',
                borderRadius: 12, padding: '13px', fontWeight: 800, fontSize: 14.5, cursor: 'pointer',
              }}>Annulla</button>
              <button onClick={() => remove(pendingDelete)} style={{
                flex: 1, border: 0, background: '#C2453B', color: '#fff',
                borderRadius: 12, padding: '13px', fontWeight: 800, fontSize: 14.5, cursor: 'pointer',
              }}>Elimina</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Costruttore ricerca (schermata unica) ---------------- */
function SearchBuilder({ onClose, onRun, initialMode, simple = false }) {
  const [zone, setZone] = useState(null);   // { lat, lng, label }
  const [zoneText, setZoneText] = useState('');
  const [area, setArea] = useState(null);   // [{lat,lng}...] area disegnata
  // Nome del luogo scelto (comune/città/indirizzo): lo conserviamo anche quando la
  // zona diventa un poligono di CONFINE (comune) → così la label NON diventa
  // "Area disegnata". '' = area davvero disegnata a mano. Marina 2026-07-14.
  const [placeLabel, setPlaceLabel] = useState('');
  const [pickerOpen, setPickerOpen] = useState(initialMode === 'draw'); // 'draw' → apri subito la mappa
  const [zoneLoading, setZoneLoading] = useState(false); // carico il confine reale della zona
  const [verdetti, setVerdetti] = useState([]);
  const [tipi, setTipi] = useState([]);
  const [prezzoMax, setPrezzoMax] = useState('');
  const [mqMin, setMqMin] = useState('');
  const [openFilter, setOpenFilter] = useState(null); // 'verdetto'|'tipologia'|'prezzo'|'superficie'
  const [geoBusy, setGeoBusy] = useState(false);

  // "Cerca intorno a me": geolocalizza e imposta la zona sulla posizione utente.
  const aroundMe = () => {
    if (!('geolocation' in navigator)) return;
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        const d = 0.05; // ~5-6 km attorno all'utente
        const viewport = { south: lat - d, west: lng - d * 1.4, north: lat + d, east: lng + d * 1.4 };
        setArea(null);
        setZone({ lat, lng, label: 'Intorno a te', viewport });
        setZoneText('Intorno a te');
        setPlaceLabel('Intorno a te');
        setGeoBusy(false);
      },
      () => setGeoBusy(false),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  };

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const verdettiSummary = verdetti.map((v) => VERDETTI.find((x) => x.v === v)?.label).filter(Boolean).join(', ');
  const tipiSummary = tipi.map((id) => TIPOLOGIA_CATEGORIES.find((t) => t.id === id)?.label).filter(Boolean).join(', ');

  const params = useMemo(() => {
    const p = {};
    if (verdetti.length) p.verdetto = verdetti.join(',');
    if (tipi.length) p.tipologia_categoria = tipi.join(',');
    if (prezzoMax) p.prezzo_max = prezzoMax;
    if (mqMin) p.mq_min = mqMin;
    if (area && area.length >= 3) {
      p.area = encodeArea(area);
      // Se il poligono viene da un comune/città scelto (placeLabel valorizzato),
      // usa quel nome; solo un'area disegnata a mano resta "Area disegnata".
      p.center_label = placeLabel || 'Area disegnata';
    } else if (zone) {
      p.center_lat = zone.lat; p.center_lng = zone.lng;
      if (zone.label) p.center_label = zone.label;
      if (zone.viewport) p.vp = encodeVp(zone.viewport);
    }
    return p;
  }, [verdetti, tipi, prezzoMax, mqMin, zone, area, placeLabel]);

  // Titolo = solo la zona (i filtri stanno nei chip sotto). Marina 2026-07-12.
  // Un comune scelto (placeLabel) tiene il suo nome anche se è diventato un poligono.
  const autoLabel = useMemo(() => {
    if (placeLabel) return placeLabel.split(',')[0].trim();
    if (area && area.length >= 3) return 'Area disegnata';
    if (zone?.label) return zone.label.split(',')[0].trim();
    return 'Tutta Italia';
  }, [zone, area, placeLabel]);

  const chip = (active, color) => ({
    padding: '9px 14px', borderRadius: 999, cursor: 'pointer', fontSize: 13.5, fontWeight: 700,
    border: `1.5px solid ${active ? (color || '#1F2937') : '#E4E6EA'}`,
    background: active ? (color || '#1F2937') : '#fff', color: active ? '#fff' : '#3A3F45',
    display: 'inline-flex', alignItems: 'center', gap: 7,
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 5000, background: '#fff', display: 'flex', flexDirection: 'column', fontFamily: FONT }}>
      {/* header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
        paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))', borderBottom: '1px solid #EEF0F2',
      }}>
        <button onClick={onClose} aria-label="Indietro" style={{
          border: 0, background: '#F1F3F5', width: 36, height: 36, borderRadius: 10, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1F2937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <div style={{ fontSize: 17, fontWeight: 900, color: '#1F2937' }}>
          {simple ? (initialMode === 'draw' ? "Disegna un'area" : 'Ricerca per zona') : 'Nuova ricerca'}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 18px 24px' }}>
        {/* Zona */}
        <div style={{ fontSize: 14, fontWeight: 800, color: '#1F2937', marginBottom: 8 }}>Dove</div>
        <AddressAutocomplete
          value={zoneText}
          onChange={setZoneText}
          onSelect={async ({ lat, lng, label, viewport }) => {
            setZoneText(label || '');
            setPlaceLabel(label || '');   // conserva il nome scelto (comune/città)
            setArea(null); setZone(null);
            // Carico il confine reale PRIMA di mostrare l'anteprima, così non si vede
            // il rettangolo che poi diventa poligono. Marina 2026-07-12.
            setZoneLoading(true);
            let poly = null;
            try { poly = await fetchPlaceBoundary(label ? label.split(',')[0] : `${lat},${lng}`); } catch { /* noop */ }
            if (poly && poly.length >= 4) setArea(poly);
            else setZone({ lat, lng, label, viewport });
            setZoneLoading(false);
          }}
          placeholder="Città, indirizzo o regione — es. Milano"
          inputClassName="pr-input"
        />
        {/* In modalità semplificata "Ricerca per zona" mostriamo SOLO l'input della
            zona: niente scorciatoie disegna-area / intorno a me. Marina 2026-07-14. */}
        {!simple && (<>
        <button onClick={() => setPickerOpen(true)} style={{
          marginTop: 10, width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          border: '1.5px dashed #C9D6EE', background: '#F7FAFF', borderRadius: 12, padding: '12px 14px',
          cursor: 'pointer', textAlign: 'left', color: '#1F6FEB', fontWeight: 700, fontSize: 13.5,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1F6FEB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><circle cx="11" cy="11" r="2" /></svg>
          Scegli o disegna la zona sulla mappa
        </button>
        <button onClick={aroundMe} disabled={geoBusy} style={{
          marginTop: 10, width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          border: '1.5px dashed #C9D6EE', background: '#F7FAFF', borderRadius: 12, padding: '12px 14px',
          cursor: geoBusy ? 'default' : 'pointer', textAlign: 'left', color: '#1F6FEB', fontWeight: 700, fontSize: 13.5,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1F6FEB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2.6" fill="#1F6FEB" stroke="none" /><path d="M12 1.5v3M12 19.5v3M1.5 12h3M19.5 12h3" /></svg>
          {geoBusy ? 'Individuo la tua posizione…' : 'Cerca intorno a me'}
        </button>
        </>)}
        {zoneLoading && (
          <div style={{
            marginTop: 12, height: 168, borderRadius: 14, background: '#F1F3F5',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            color: '#6B7280', fontSize: 14, fontWeight: 700,
          }}>
            <span style={{ width: 18, height: 18, borderRadius: '50%', border: '3px solid #D9DEE3', borderTopColor: '#1F6FEB', animation: 'zlSpin .8s linear infinite' }} />
            <style>{'@keyframes zlSpin{to{transform:rotate(360deg)}}'}</style>
            Individuo la zona…
          </div>
        )}
        {!zoneLoading && ((area && area.length >= 3) || zone) && (
          <div style={{ marginTop: 12 }}>
            <AreaPreview
              area={(area && area.length >= 3) ? area : null}
              bounds={(!area && zone?.viewport) ? zone.viewport : null}
              center={(!area && zone && !zone.viewport) ? { lat: zone.lat, lng: zone.lng } : null}
              onClick={() => setPickerOpen(true)}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#1F2937' }}>
                {placeLabel ? placeLabel.split(',')[0]
                  : (area && area.length >= 3) ? '✏️ Area disegnata'
                  : (zone?.label || 'Zona selezionata').split(',')[0]}
              </span>
              <button onClick={() => { setArea(null); setZone(null); setZoneText(''); setPlaceLabel(''); }}
                style={{ border: 0, background: 'transparent', color: '#C2453B', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                Rimuovi
              </button>
            </div>
          </div>
        )}

        {pickerOpen && (
          <ZonePickerMap
            initialCenter={zone ? { lat: zone.lat, lng: zone.lng } : null}
            drawFirst={initialMode === 'draw'}
            onClose={() => setPickerOpen(false)}
            onConfirm={(z) => {
              if (z.area) {
                // area disegnata a mano → nessun nome luogo → label "Area disegnata"
                setArea(z.area); setZone(null); setZoneText(z.center_label || 'Area disegnata'); setPlaceLabel('');
              } else {
                setArea(null); setZone({ lat: z.center_lat, lng: z.center_lng, label: z.center_label }); setZoneText(z.center_label || ''); setPlaceLabel(z.center_label || '');
              }
              setPickerOpen(false);
            }}
          />
        )}

        {/* Filtri collassati (nome → sheet col valore, come sulla mappa ma in verticale).
            In modalità semplificata (ricerca per zona/disegna) NON si mostrano: solo zona. */}
        {!simple && (<>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#1F2937', margin: '24px 0 8px' }}>Filtri</div>
        <div style={{ border: '1px solid #EEF0F2', borderRadius: 14, overflow: 'hidden' }}>
          <FilterRow label="Verdetto"   value={verdettiSummary}                  onClick={() => setOpenFilter('verdetto')} />
          <FilterRow label="Tipologia"  value={tipiSummary}                      onClick={() => setOpenFilter('tipologia')} />
          <FilterRow label="Prezzo"     value={prezzoMax ? euro(prezzoMax) : ''} onClick={() => setOpenFilter('prezzo')} />
          <FilterRow label="Superficie" value={mqMin ? 'da ' + mqMin + ' m²' : ''} onClick={() => setOpenFilter('superficie')} last />
        </div>
        </>)}

        {!simple && openFilter && (
          <BuilderSheet
            filter={BUILDER_FILTERS[openFilter]}
            multiSelected={openFilter === 'verdetto' ? verdetti : openFilter === 'tipologia' ? tipi : null}
            singleValue={openFilter === 'prezzo' ? prezzoMax : openFilter === 'superficie' ? mqMin : null}
            onToggleMulti={(v) => { if (openFilter === 'verdetto') toggle(verdetti, setVerdetti, v); else toggle(tipi, setTipi, v); }}
            onSelectSingle={(v) => { if (openFilter === 'prezzo') setPrezzoMax(v); else setMqMin(v); setOpenFilter(null); }}
            onClear={() => { if (openFilter === 'verdetto') setVerdetti([]); else if (openFilter === 'tipologia') setTipi([]); else if (openFilter === 'prezzo') setPrezzoMax(''); else setMqMin(''); }}
            onClose={() => setOpenFilter(null)}
          />
        )}

      </div>

      {/* footer */}
      <div style={{
        borderTop: '1px solid #EEF0F2', padding: '12px 16px',
        paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
      }}>
        <button onClick={() => onRun(params, autoLabel, false)} style={{
          width: '100%', border: 0, background: '#1F6FEB', color: '#fff', borderRadius: 14,
          padding: '15px', fontWeight: 900, fontSize: 15.5, cursor: 'pointer',
        }}>{simple ? 'Conferma' : 'Vedi risultati sulla mappa'}</button>
      </div>
    </div>
  );
}
